import React, { useMemo } from "react";

import { useQuery } from "@apollo/client";
import moment from "moment-timezone";
import { Link } from "react-router-dom";

import {
  formatBytes,
  formatDuration,
  formatFullTransactionID,
  formatNumber,
  formatTimeAgo,
  formatTimestampLong,
  formatTimestampShort,
} from "utils/format";

import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import SQL from "ui/SQL";
import GraphSection from "ui/Graph/GraphSection";
import Graph from "ui/Graph";

import { Data } from "ui/Graph/util";

import SLOW_QUERY_STATS_QUERY from "./Query.slowQueryStats.graphql";

import styles from "./style.module.scss";

import { IssueDetailQuery } from "../gql/Query.generated";

type IssueType = IssueDetailQuery["getIssue"];

type IssueReferent =
  IssueDetailQuery["getIssue"]["references"][number]["referent"];

type IssueReferenceIndex = Extract<
  IssueReferent,
  { __typename?: "SchemaIndex" }
>;
type IssueReferenceBackend = Extract<IssueReferent, { __typename?: "Backend" }>;
type IssueReferenceQuery = Extract<IssueReferent, { __typename?: "Query" }>;
type IssueReferenceReplicationFollower = Extract<
  IssueReferent,
  { __typename?: "ReplicationFollower" }
>;
type IssueReferenceVacuumRun = Extract<
  IssueReferent,
  { __typename?: "VacuumRun" }
>;
type IssueReferenceTable = Extract<
  IssueReferent,
  { __typename?: "SchemaTable" }
>;

import { AreaSeries, ThresholdSeries } from "ui/Graph/Series";
import ExpandableSQL from "ui/ExpandableSQL";
import { useRoutes } from "utils/routes";
import { retention } from "utils/limits";
import Grid from "ui/Grid";
import { formatDurationPrecise } from "utils/format";
import Tip from "ui/Tip";
import { labelXminHeldBackBy } from "utils/vacuum";
import XminHorizonGraph from "features/Vacuum/XminHorizonGraph";
// eslint-disable-next-line no-restricted-imports -- pre-existing cross-feature deep import
import FreezingStatsGraph from "features/Vacuum/VacuumAdvisor/FreezingStats/FreezingStatsGraph";
import capitalize from "lodash/capitalize";
import { useCachedTimestamp } from "utils/hooks";
import DateRangeGraph from "ui/Graph/DateRangeGraph";
// eslint-disable-next-line no-restricted-imports -- pre-existing cross-feature deep import
import { useReplicationDetailsGraphQuery } from "features/System/ReplicationDetails/Graph/gql/Query.generated";

type Props = {
  issue: IssueType;
  checkGroup: string;
  checkName: string;
};

const IssueReferencesPanel: React.FunctionComponent<Props> = ({
  issue,
  checkGroup,
  checkName,
}) => {
  if (issue.references.length === 0) {
    return null;
  }

  switch (checkGroup + "/" + checkName) {
    case "connections/active_query":
      return <ActiveQueryReferencesPanel issue={issue} />;
    case "connections/idle_transaction":
      return <IdleTransactionReferencesPanel issue={issue} />;
    case "connections/blocking_query":
      return <BlockingQueryReferencesPanel issue={issue} />;
    case "queries/slowness":
      return <SlowQueryReferencesPanel issue={issue} />;
    case "replication/high_lag":
      return <ReplicationReferencesPanel issue={issue} />;
    case "schema/index_invalid":
      return <IndexInvalidReferencesPanel issue={issue} />;
    case "schema/index_unused":
      return <IndexUnusedReferencesPanel issue={issue} />;
    case "settings/enable_features":
      return <EnableFeaturesReferencesPanel issue={issue} />;
    case "settings/stats":
      return <StatsReferencesPanel issue={issue} />;
    case "vacuum/inefficient_index_phase":
      return <VacuumInefficientIndexPhaseReferencesPanel issue={issue} />;
    case "vacuum/xmin_horizon":
      return <VacuumXminHorizonReferencesPanel issue={issue} />;
    case "vacuum/txid_wraparound":
      return <VacuumTxidWraparoundReferencesPanel issue={issue} />;
    case "vacuum/mxid_wraparound":
      return <VacuumMxidWraparoundReferencesPanel issue={issue} />;
    default:
      return null;
  }
};

const ActiveQueryReferencesPanel: React.FunctionComponent<{
  issue: IssueType;
}> = ({ issue }) => {
  const refData = useMemo(() => {
    const now =
      issue.state == "resolved" ? moment.unix(issue.updatedAt) : moment();
    return issue.references.map((ref) => {
      const pid = (ref.referent as IssueReferenceBackend).pid;
      const path = ref.url;
      const { details } = ref;
      const queryText = details.query_text ?? ref.queryText;
      const queryAge =
        details.query_age ??
        now.diff(moment.unix(details.query_start), "seconds");

      return {
        pid,
        path,
        queryAge,
        applicationName: details.application_name,
        queryText,
      };
    });
  }, [issue.references, issue.state, issue.updatedAt]);
  return (
    <Panel title="Queries">
      <Grid
        striped
        data={refData}
        columns={[
          {
            field: "pid",
            header: "PID",
            width: "80px",
            renderer: function PIDCell({ rowData }) {
              return <Link to={rowData.path}>{rowData.pid}</Link>;
            },
          },
          {
            field: "queryAge",
            header: "Duration",
            width: "100px",
            renderer: ({ fieldData }) => formatDuration(fieldData),
          },
          { field: "applicationName", header: "Application", width: "140px" },
          {
            field: "queryText",
            header: "SQL",
            width: "1fr",
            nullValue: "<query text not available>",
            renderer: function QueryCell({ fieldData }) {
              return <ExpandableSQL sql={fieldData} />;
            },
            tip: "The query may be truncated based on track_activity_query_size",
          },
        ]}
      />
    </Panel>
  );
};

const IdleTransactionReferencesPanel: React.FunctionComponent<{
  issue: IssueType;
}> = ({ issue }) => {
  const refData = useMemo(() => {
    const now =
      issue.state == "resolved" ? moment.unix(issue.updatedAt) : moment();
    return issue.references.map((ref) => {
      const pid = (ref.referent as IssueReferenceBackend).pid;
      const path = ref.url;
      const { details } = ref;
      const xactStartTime = details.xact_idle_start
        ? moment.unix(details.xact_idle_start)
        : now.subtract(details.xact_idle_age, "seconds");

      return {
        pid,
        path,
        xactStartTime,
      };
    });
  }, [issue.references, issue.state, issue.updatedAt]);

  return (
    <Panel title="Idle transactions">
      <Grid
        striped
        data={refData}
        columns={[
          {
            field: "pid",
            header: "PID",
            width: "100px",
            renderer: function PidCell({ rowData, fieldData }) {
              return <Link to={rowData.path}>{fieldData}</Link>;
            },
          },
          {
            field: "xactStartTime",
            header: "Transaction start time",
            width: "180px",
            nullValue: "n/a",
            renderer: function XactStartTimeCell({ fieldData }) {
              return formatTimestampLong(fieldData);
            },
          },
          {
            field: "xactStartTime",
            header: "Transaction started",
            width: "1fr",
            nullValue: "n/a",
            renderer: function XactStartTimeAgoCell({ fieldData }) {
              return capitalize(formatTimeAgo(fieldData));
            },
          },
        ]}
      />
    </Panel>
  );
};

const BlockingQueryReferencesPanel: React.FunctionComponent<{
  issue: IssueType;
}> = ({ issue }) => {
  const { databaseBackends, databaseBackendsAtTime } = useRoutes();
  let databaseBackendsPath = databaseBackends(issue.databaseId);
  if (issue.state == "resolved") {
    // When the issue is already resolved, try to show the link with
    // the time that the issue was happening
    const from = issue.activity.at(0).occurredAt;
    const to = issue.activity.at(-1).occurredAt;
    // Don't set the time if the range is already out of the retention
    if (
      moment().diff(moment.unix(to)) < retention.connections.asMilliseconds()
    ) {
      databaseBackendsPath = databaseBackendsAtTime(issue.databaseId, from, to);
    }
  }
  const refData = useMemo(() => {
    const now =
      issue.state == "resolved" ? moment.unix(issue.updatedAt) : moment();
    return issue.references.map((ref) => {
      const pid = (ref.referent as IssueReferenceBackend).pid;
      const path = ref.url;
      const { details } = ref;
      const queryText = details.query_text ?? ref.queryText;
      const blockedAge =
        details.blocked_age ??
        now.diff(moment.unix(details.blocked_start), "seconds");

      return {
        pid,
        path,
        blockedAge,
        blockedCount: details.blocked_count,
        applicationName: details.application_name,
        queryText,
      };
    });
  }, [issue.references, issue.state, issue.updatedAt]);
  return (
    <Panel title="Queries">
      <Grid
        striped
        data={refData}
        columns={[
          {
            field: "pid",
            header: "PID",
            width: "68px",
            renderer: function PIDCell({ rowData }) {
              return <Link to={rowData.path}>{rowData.pid}</Link>;
            },
          },
          {
            field: "blockedCount",
            header: "Blocking",
            width: "100px",
            renderer: function BlockingCell({ fieldData }) {
              return <Link to={databaseBackendsPath}>{fieldData} queries</Link>;
            },
          },
          {
            field: "blockedAge",
            header: "Blocking For",
            width: "130px",
            renderer: ({ fieldData }) => formatDuration(fieldData),
          },
          { field: "applicationName", header: "Application", width: "120px" },
          {
            field: "queryText",
            header: "SQL",
            width: "1fr",
            nullValue: "<query text not available>",
            renderer: function QueryCell({ fieldData }) {
              return <ExpandableSQL sql={fieldData} />;
            },
            tip: "The query may be truncated based on track_activity_query_size",
          },
        ]}
      />
    </Panel>
  );
};

const VacuumInefficientIndexPhaseReferencesPanel: React.FunctionComponent<{
  issue: IssueType;
}> = ({ issue }) => {
  const { database, databaseTable } = useRoutes();

  const refData = issue.references.map((ref) => {
    const vacuumRun = ref.referent as IssueReferenceVacuumRun;
    const vacuumRunPath = ref.url;
    const { details } = ref;

    const databasePath = database(vacuumRun.databaseId);
    const tablePath = databaseTable(
      vacuumRun.databaseId,
      vacuumRun.schemaTableId,
    );

    return {
      identity: vacuumRun.identity,
      vacuumRunPath,
      vacuumStart: vacuumRun.vacuumStart,
      vacuumEnd: vacuumRun.vacuumEnd,
      databasePath,
      databaseName: details.database_name,
      schemaName: details.schema_name,
      tableName: details.table_name,
      tablePath,
      indexPhases: details.index_vacuum_count,
    };
  });

  function TimeCell({ fieldData }: { fieldData: number }) {
    return formatTimestampLong(moment.unix(fieldData));
  }

  return (
    <Panel title="VACUUM Runs">
      <Grid
        striped
        data={refData}
        columns={[
          {
            field: "identity",
            header: "ID",
            width: "minmax(8%,50px)",
            renderer: function IDCell({ fieldData, rowData }) {
              return <Link to={rowData.vacuumRunPath}>{fieldData}</Link>;
            },
          },
          {
            field: "vacuumStart",
            header: "Start Time",
            width: "minmax(15%,180px)",
            renderer: TimeCell,
          },
          {
            field: "vacuumEnd",
            header: "End Time",
            width: "minmax(15%,180px)",
            renderer: TimeCell,
          },
          {
            field: "databaseName",
            header: "Database",
            width: "minmax(8%,100px)",
            renderer: function DatabaseCell({ rowData, fieldData }) {
              return <Link to={rowData.databasePath}>{fieldData}</Link>;
            },
          },
          {
            field: "schemaName",
            header: "Schema",
            width: "minmax(8%,100px)",
          },
          {
            field: "tableName",
            header: "Table",
            width: "minmax(15%,1fr)",
            renderer: function TableCell({ rowData, fieldData }) {
              return <Link to={rowData.tablePath}>{fieldData}</Link>;
            },
          },
          {
            field: "indexPhases",
            header: "Index Phases",
            width: "minmax(10%,120px)",
            renderer: function IndexPhasesCell({ fieldData }) {
              return formatNumber(fieldData);
            },
          },
        ]}
      />
    </Panel>
  );
};

const IndexUnusedReferencesPanel: React.FunctionComponent<{
  issue: IssueType;
}> = ({ issue }) => {
  const gridData = issue.references.map((ref) => {
    return {
      name: ref.name,
      path: ref.url,
      size: (ref.referent as IssueReferenceIndex).sizeBytes,
      lastUsedAt: ref.details.last_used_at,
    };
  });
  return (
    <Panel title="Unused indexes">
      <Grid
        striped
        data={gridData}
        columns={[
          {
            field: "name",
            header: "Index",
            width: "1fr",
            renderer: function IndexCell({ rowData, fieldData }) {
              return <Link to={rowData.path}>{fieldData}</Link>;
            },
          },
          {
            field: "size",
            header: "Size",
            width: "minmax(10%,120px)",
            style: "number",
            renderer: function SizeCell({ fieldData }) {
              return formatBytes(fieldData);
            },
          },
          {
            field: "lastUsedAt",
            header: "Last Used",
            width: "minmax(10%,120px)",
            nullValue: "-",
          },
        ]}
      />
    </Panel>
  );
};

const IndexInvalidReferencesPanel: React.FunctionComponent<{
  issue: IssueType;
}> = ({ issue }) => {
  const gridData = issue.references.map((ref) => {
    return {
      name: ref.name,
      path: ref.url,
      size: (ref.referent as IssueReferenceIndex).sizeBytes,
    };
  });
  return (
    <Panel title="Invalid indexes">
      <Grid
        striped
        data={gridData}
        columns={[
          {
            field: "name",
            header: "Index",
            width: "1fr",
            renderer: function IndexCell({ rowData, fieldData }) {
              return <Link to={rowData.path}>{fieldData}</Link>;
            },
          },
          {
            field: "size",
            header: "Size",
            width: "minmax(10%,120px)",
            style: "number",
            renderer: function SizeBytes({ fieldData }) {
              return formatBytes(fieldData);
            },
          },
        ]}
      />
    </Panel>
  );
};

const SlowQueryReferencesPanel: React.FunctionComponent<{
  issue: IssueType;
}> = ({ issue }) => {
  const endTs = useCachedTimestamp(moment(), moment.duration(1, "minute"));
  const startTs = endTs.clone().subtract(1, "day");
  const slowQueryRef = issue.references[0];
  const slowQueryId = (slowQueryRef.referent as IssueReferenceQuery).id;
  const { data, loading, error } = useQuery(SLOW_QUERY_STATS_QUERY, {
    variables: {
      databaseId: issue.databaseId,
      queryId: slowQueryId,
      endTs: endTs.unix(),
      startTs: startTs.unix(),
    },
  });
  let result = data?.getQueryDetailStats as unknown as Data;
  const noData = !!result && result.noData != null;

  if (result && !noData) {
    const config = issue.checkConfig.settings;
    const thresholdMs = config["threshold_ms"];
    result = {
      ...result,
      threshold: result.calls.map(([ts]) => [ts, thresholdMs]),
    };
  }

  const normalizedQuery = data?.getQueryDetails?.normalizedQuery;
  const normalizedQueryScanTokens =
    data?.getQueryDetails?.normalizedQueryScanTokens;
  const hasFullQuery = normalizedQuery && normalizedQueryScanTokens;

  return (
    <Panel title="Slow Query">
      <PanelSection>
        {hasFullQuery ? (
          <ExpandableSQL
            sql={normalizedQuery}
            databaseId={issue.databaseId}
            scanTokens={normalizedQueryScanTokens}
          />
        ) : (
          <SQL sql={slowQueryRef.queryText} />
        )}
      </PanelSection>
      <GraphSection noData={noData} loading={loading} error={error}>
        <Graph
          data={result}
          axes={{
            left: {
              format: "duration ms",
              tipFormat: (y: number): string => y.toFixed(1) + " ms",
            },
            right: {
              format: "count",
              tipFormat: (y: number): string => y.toFixed(1) + "/min",
            },
          }}
          series={[
            {
              type: ThresholdSeries,
              key: "threshold",
              label: "Check Threshold",
              tipLabel: "Threshold",
              color: "red",
            },
            { key: "avgTime", color: "yellowGreen", label: "Avg Total Time" },
            { key: "avgIoTime", color: "turquoise", label: "Avg I/O Time" },
            {
              key: "calls",
              color: "orange",
              label: "Calls",
              yAxis: "right",
              className: styles.newCalls,
            },
          ]}
        />
      </GraphSection>
    </Panel>
  );
};

const ReplicationReferencesPanel: React.FunctionComponent<{
  issue: IssueType;
}> = ({ issue }) => {
  let endTs = useCachedTimestamp(moment(), moment.duration(1, "minute"));
  let startTs = endTs.clone().subtract(1, "day");
  if (issue.state == "resolved") {
    // When the issue is already resolved, try to show the graph with
    // the time that the issue was happening
    startTs = moment.unix(issue.activity.at(0).occurredAt);
    endTs = moment.unix(issue.activity.at(-1).occurredAt);
  }
  const replicationFollowerRef = issue.references[0];
  const replicationFollowerId = (
    replicationFollowerRef.referent as IssueReferenceReplicationFollower
  ).id;

  const { data, loading, error } = useReplicationDetailsGraphQuery({
    variables: {
      serverId: issue.server.humanId,
      replicationFollowerId,
      endTs: endTs.unix(),
      startTs: startTs.unix(),
    },
  });

  let result = data?.getReplicationFollowerStats as unknown as Data;
  const noData = !!result && result.noData != null;
  if (result && !noData) {
    const config = issue.checkConfig.settings;
    const warningThresholdBytes = config["warning_threshold_mb"] * 1024 * 1024;
    const criticalThresholdBytes =
      config["critical_threshold_mb"] * 1024 * 1024;
    result = {
      ...result,
      warningThreshold: result.remoteByteLag.map(([ts]) => [
        ts,
        warningThresholdBytes,
      ]),
      criticalThreshold: result.remoteByteLag.map(([ts]) => [
        ts,
        criticalThresholdBytes,
      ]),
    };
  }

  return (
    <Panel title="Replication Lag">
      <GraphSection loading={loading} error={error}>
        <DateRangeGraph
          data={result}
          axes={{ left: { format: formatBytes } }}
          series={[
            {
              key: "totalByteLag",
              label: "Total Byte Lag",
              color: "mediumSeaGreen",
            },
            {
              key: "remoteByteLag",
              label: "Remote Byte Lag",
              type: AreaSeries,
              color: "lime",
            },
            {
              key: "localByteLag",
              label: "Local Byte Lag",
              type: AreaSeries,
              color: "yellowGreen",
            },
            {
              type: ThresholdSeries,
              key: "warningThreshold",
              label: "Check Warning Threshold",
              tipLabel: "Warning Threshold",
              color: "orange",
            },
            {
              type: ThresholdSeries,
              key: "criticalThreshold",
              label: "Check Critical Threshold",
              tipLabel: "Critical Threshold",
              color: "red",
            },
          ]}
        />
      </GraphSection>
    </Panel>
  );
};

const EnableFeaturesReferencesPanel: React.FunctionComponent<{
  issue: IssueType;
}> = ({ issue }) => {
  const gridData = issue.references.map((ref) => {
    return {
      name: ref.name,
      path: ref.url,
      value: "off",
    };
  });
  return (
    <Panel title="Disabled planner settings">
      <Grid
        striped
        data={gridData}
        columns={[
          {
            field: "name",
            header: "Setting",
            width: "1fr",
            renderer: function SettingCell({ rowData, fieldData }) {
              return (
                <Link to={rowData.path}>
                  <strong>{fieldData}</strong>
                </Link>
              );
            },
          },
          {
            field: "value",
            header: "Current Value",
            width: "minmax(10%,120px)",
          },
        ]}
      />
    </Panel>
  );
};

const StatsReferencesPanel: React.FunctionComponent<{
  issue: IssueType;
}> = ({ issue }) => {
  const gridData = issue.references.map((ref) => {
    return {
      name: ref.name,
      path: ref.url,
      value: "off",
    };
  });
  return (
    <Panel title="Disabled tracking settings">
      <Grid
        striped
        data={gridData}
        columns={[
          {
            field: "name",
            header: "Setting",
            width: "1fr",
            renderer: function SettingCell({ rowData, fieldData }) {
              return (
                <Link to={rowData.path}>
                  <strong>{fieldData}</strong>
                </Link>
              );
            },
          },
          {
            field: "value",
            header: "Current Value",
            width: "minmax(10%,120px)",
          },
        ]}
      />
    </Panel>
  );
};

const VacuumXminHorizonReferencesPanel: React.FunctionComponent<{
  issue: IssueType;
}> = ({ issue }) => {
  const { databaseTableVacuum } = useRoutes();
  const endTs = useCachedTimestamp(moment(), moment.duration(1, "minute"));
  const startTs = endTs.clone().subtract(7, "days");

  type HeldBackBy = {
    type: string;
    xmin: number;
    assigned_at: number;
  };
  const heldBackBy = issue.details.held_back_by as HeldBackBy[];
  const config = issue.checkConfig.settings;
  const thresholdHours = config["behind_hours"];
  const vacuumsList = issue.references.map((ref) => {
    const details = ref.details;
    const table = ref.referent as IssueReferenceTable;
    return {
      name: ref.name,
      url: databaseTableVacuum(table.databaseId, table.id),
      oldest_xmin: details.oldest_xmin,
      not_removable: details.not_removable,
      autovacuum_count: details.autovacuum_count,
    };
  });
  return (
    <>
      <Panel title="Xmin Horizon Assignment Age Last 7 Days (in hours)">
        <XminHorizonGraph
          serverId={issue.server.humanId}
          startTs={startTs.unix()}
          endTs={endTs.unix()}
          thresholdHours={thresholdHours}
        />
      </Panel>
      <Panel title="Identified Causes">
        <Grid
          data={heldBackBy}
          columns={[
            {
              field: "type",
              header: "Cause",
              width: "1fr",
              renderer: function CauseCell({ fieldData }) {
                return (
                  <>
                    {labelXminHeldBackBy(fieldData)}
                    {fieldData === "backend" && heldBackBy.length > 1 && (
                      // Show special tip for backend when there are multiple causes
                      <Tip
                        content="Long-running transaction is likely led by other causes you can see in the list. Please check out other causes first."
                        className="ml-1"
                      />
                    )}
                  </>
                );
              },
            },
            {
              field: "xmin",
              header: "Xmin Horizon",
              width: "160px",
              style: "number",
              renderer: ({ fieldData }) => formatFullTransactionID(fieldData),
            },
            {
              field: "assigned_at",
              header: "Xmin Assigned At",
              width: "400px",
              nullValue: "-",
              renderer: function XminAssingedAtCell({ fieldData }) {
                // takes ms
                const timeAgo = ` (${formatDurationPrecise(
                  (moment().unix() - fieldData) * 1000,
                )} ago)`;
                return formatTimestampShort(moment.unix(fieldData)) + timeAgo;
              },
            },
          ]}
        />
      </Panel>
      <Panel title={`Blocked VACUUMs Last ${thresholdHours} Hours`}>
        <Grid
          data={vacuumsList}
          defaultSortBy="not_removable"
          columns={[
            {
              field: "name",
              header: "Table",
              width: "1fr",
              renderer: function TableCell({ rowData }) {
                return (
                  <Link to={rowData.url}>
                    <strong>{rowData.name}</strong>
                  </Link>
                );
              },
            },
            {
              field: "oldest_xmin",
              header: "Oldest Xmin",
              width: "180px",
              style: "number",
              tip: "The xmin horizon. VACUUMs were only able to clean up dead rows until this point.",
              nullValue: "-",
              renderer: function OldestXminCell({ fieldData }) {
                return formatFullTransactionID(fieldData);
              },
            },
            {
              field: "not_removable",
              header: "Not Removable Dead Rows",
              width: "240px",
              style: "number",
              tip: `In VACUUM log line, this shows up as "X are dead but not yet removable, oldest xmin: Y"`,
              nullValue: "-",
              renderer: function NotRemovableCell({ fieldData }) {
                return formatNumber(fieldData);
              },
              defaultSortOrder: "DESC",
            },
            {
              field: "autovacuum_count",
              header: "Blocked VACUUM count",
              width: "200px",
              style: "number",
              tip: "Number of VACUUMs that had not removable dead rows in the last 24 hours",
              nullValue: "-",
              renderer: function VacuumCountCell({ fieldData }) {
                return formatNumber(fieldData);
              },
            },
          ]}
          pageSize={5}
        />
      </Panel>
    </>
  );
};

const VacuumTxidWraparoundReferencesPanel: React.FunctionComponent<{
  issue: IssueType;
}> = ({ issue }) => {
  return (
    <Panel title="Transaction ID Utilization per Database">
      <FreezingStatsGraph serverId={issue.server.humanId} type="txid" />
    </Panel>
  );
};

const VacuumMxidWraparoundReferencesPanel: React.FunctionComponent<{
  issue: IssueType;
}> = ({ issue }) => {
  return (
    <Panel title="Multixact ID Utilization per Database">
      <FreezingStatsGraph serverId={issue.server.humanId} type="mxid" />
    </Panel>
  );
};

export default IssueReferencesPanel;
